import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../../api';

/**
 * Fetch comprehensive statistics for a widget.
 */
export const fetchWidgetStatistics = async (widgetId, token, days = 30, workspaceId = null) => {
  try {
    let resolvedWorkspaceId = workspaceId;

    if (!resolvedWorkspaceId) {
      const widgetResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/widget_config/${widgetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      resolvedWorkspaceId = widgetResponse.data.data?.widget_linked_workspace;
    }

    if (!resolvedWorkspaceId) {
      throw new Error('Workspace ID not found for this widget');
    }

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days + 1);

    const allSessionsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        filter: JSON.stringify({
          _and: [
            { linked_workspace: { _nnull: true } },
            { linked_workspace: { _eq: resolvedWorkspaceId } },
            { date_created: { _gte: startDate.toISOString() } },
            { date_created: { _lte: endDate.toISOString() } },
            { overall_sentiment: { _nnull: true } }
          ]
        }),
        fields: ['id', 'date_created'],
        limit: 1000
      }
    });

    const sessions = allSessionsResponse.data.data || [];
    const sessionIds = sessions.map((s) => s.id);

    const totalSessions = sessionIds.length;
    let totalMessages = 0;

    let interval = 1;
    let intervalLabel = 'day';
    if (days > 90 && days <= 180) {
      interval = 3;
      intervalLabel = '3 days';
    } else if (days > 180) {
      interval = 7;
      intervalLabel = 'week';
    }

    const sessionsByDate = {};
    const messagesByDate = {};

    for (let i = 0; i < days; i += interval) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateStr = currentDate.toISOString().split('T')[0];
      sessionsByDate[dateStr] = 0;
      messagesByDate[dateStr] = 0;
    }

    sessions.forEach((session) => {
      if (!session.date_created) return;
      const sessionDate = new Date(session.date_created);
      const dateStr = sessionDate.toISOString().split('T')[0];

      if (sessionsByDate[dateStr] !== undefined) {
        sessionsByDate[dateStr] += 1;
      } else {
        const dates = Object.keys(sessionsByDate).sort();
        const closestDate = dates.reduce((prev, curr) => {
          const prevDiff = Math.abs(new Date(prev) - sessionDate);
          const currDiff = Math.abs(new Date(curr) - sessionDate);
          return prevDiff < currDiff ? prev : curr;
        });
        sessionsByDate[closestDate] += 1;
      }
    });

    if (sessionIds.length > 0) {
      try {
        const CHUNK_SIZE = 50;
        const PAGE_SIZE = 100;
        let allMessages = [];

        for (let i = 0; i < sessionIds.length; i += CHUNK_SIZE) {
          const sessionChunk = sessionIds.slice(i, i + CHUNK_SIZE);
          let page = 1;
          let hasMore = true;

          while (hasMore) {
            const messagesResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_messages`, {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                filter: JSON.stringify({
                  _and: [
                    { session_id: { _in: sessionChunk } },
                    { date_created: { _gte: startDate.toISOString() } },
                    { date_created: { _lte: endDate.toISOString() } }
                  ]
                }),
                fields: ['id', 'date_created', 'session_id'],
                limit: PAGE_SIZE,
                page
              }
            });

            const messages = messagesResponse.data.data || [];
            allMessages = [...allMessages, ...messages];
            totalMessages += messages.length;
            hasMore = messages.length === PAGE_SIZE;
            page += 1;

            if (page > 10) {
              break;
            }
          }

          if (i + CHUNK_SIZE < sessionIds.length) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        allMessages.forEach((message) => {
          if (!message.date_created) return;
          const messageDate = new Date(message.date_created);
          const dateStr = messageDate.toISOString().split('T')[0];

          if (messagesByDate[dateStr] !== undefined) {
            messagesByDate[dateStr] += 1;
          } else {
            const dates = Object.keys(messagesByDate).sort();
            if (dates.length > 0) {
              const closestDate = dates.reduce((prev, curr) => {
                const prevDiff = Math.abs(new Date(prev) - messageDate);
                const currDiff = Math.abs(new Date(curr) - messageDate);
                return prevDiff < currDiff ? prev : curr;
              });
              messagesByDate[closestDate] += 1;
            }
          }
        });
      } catch (_error) {
        // Ignore message aggregation errors and return partial stats.
      }
    }

    const sessionsByDay = Object.entries(sessionsByDate)
      .map(([date, count]) => ({
        date,
        count,
        displayDate: new Date(date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short'
        })
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const messagesByDay = Object.entries(messagesByDate)
      .map(([date, count]) => ({
        date,
        count,
        displayDate: new Date(date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short'
        })
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      totalSessions,
      totalMessages,
      sessionsByDay,
      messagesByDay,
      workspaceId: resolvedWorkspaceId,
      lastUpdated: new Date().toISOString(),
      groupedBy: intervalLabel
    };
  } catch (error) {
    return {
      totalSessions: 0,
      totalMessages: 0,
      sessionsByDay: [],
      messagesByDay: [],
      lastUpdated: new Date().toISOString(),
      error: error.message
    };
  }
};

/**
 * Debug helper to inspect raw widget analytics payloads.
 */
export const debugWidgetData = async (widgetId, token) => {
  try {
    const widgetResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/widget_config/${widgetId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const widgetData = widgetResponse.data.data;
    const workspaceId = widgetData?.widget_linked_workspace;

    const sessionsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        filter: JSON.stringify({
          _and: [
            { linked_workspace: { _eq: workspaceId } },
            { linked_widget: { _eq: widgetId } }
          ]
        }),
        limit: 10,
        meta: 'filter_count'
      }
    });

    let messagesCount = 0;
    let messageSample = null;

    if (sessionsResponse.data.data?.length > 0) {
      const sessionIds = sessionsResponse.data.data.map((s) => s.id);

      try {
        const messagesResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_messages`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: JSON.stringify({
              session_id: { _in: sessionIds }
            }),
            meta: 'filter_count',
            limit: 1
          }
        });

        messagesCount = messagesResponse.data.meta?.filter_count || 0;

        if (messagesCount > 0) {
          const sampleMessageResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_messages`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              filter: JSON.stringify({
                session_id: { _in: sessionIds }
              }),
              limit: 1
            }
          });

          if (sampleMessageResponse.data.data?.length > 0) {
            messageSample = sampleMessageResponse.data.data[0];
          }
        }
      } catch (_error) {
        // Keep debug response best-effort.
      }
    }

    const allWidgetSessionsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        filter: JSON.stringify({
          linked_widget: { _nnull: true }
        }),
        meta: 'filter_count',
        limit: 1
      }
    });

    const allWidgetSessionsCount = allWidgetSessionsResponse.data.meta?.filter_count || 0;

    const allWorkspaceSessionsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        filter: JSON.stringify({
          linked_workspace: { _eq: workspaceId }
        }),
        meta: 'filter_count',
        limit: 1
      }
    });

    const allWorkspaceSessionsCount = allWorkspaceSessionsResponse.data.meta?.filter_count || 0;

    return {
      widgetData,
      workspaceId,
      sessionSample: sessionsResponse.data.data?.[0] || null,
      messageSample,
      counts: {
        widgetSessions: sessionsResponse.data.meta?.filter_count || 0,
        widgetMessages: messagesCount,
        allWidgetSessions: allWidgetSessionsCount,
        allWorkspaceSessions: allWorkspaceSessionsCount
      }
    };
  } catch (error) {
    return { error: error.message };
  }
};
